// 🔧 points_helpers.js
// ✅ Purpose: Centralized Points / Scorecard voting calculation engine
// ✅ Features:
// - Supports 100-point judged scoring
// - Validates scoring factors total exactly 100 available points
// - Calculates a single voter score from scoring factor entries
// - Aggregates all point ballots by entry
// - Calculates average overall score per entry
// - Calculates average score per scoring factor
// - Generates Top 3 overall results
// - Generates Top 3 results per scoring factor
// - Keeps Points Voting logic out of vote.js, ballot.js, leaderboard.js, audit.js, and entries.js
// - Fully block-commented for clarity and future devs

/**
 * ✅ Expected Scoring Factor Shape
 *
 * scoringFactors = [
 *   {
 *     id: "body",
 *     name: "Body",
 *     maxPoints: 15,
 *     sortOrder: 1,
 *     active: true
 *   }
 * ]
 */

/**
 * ✅ Expected Points Vote Shape
 *
 * vote = {
 *   uid: "abc123",
 *   cid: "carDocId",
 *   catid: "1",
 *   eventId: "eventDocId",
 *   votingType: "points",
 *   totalPoints: 87,
 *   scores: [
 *     {
 *       factorId: "body",
 *       factorName: "Body",
 *       pointsAwarded: 13,
 *       maxPoints: 15,
 *       checkedHighlights: ["contour", "alignment"]
 *     }
 *   ]
 * }
 */

/**
 * ✅ Safely converts a value into a usable number.
 */
export function toSafePoints(value) {
  const numberValue = Number(value);

  if (Number.isNaN(numberValue)) {
    return 0;
  }

  return numberValue;
}

/**
 * ✅ Returns only active scoring factors, sorted by sortOrder then name.
 */
export function getActiveScoringFactors(scoringFactors = []) {
  return scoringFactors
    .filter((factor) => factor?.active !== false)
    .sort((a, b) => {
      const sortA = toSafePoints(a?.sortOrder);
      const sortB = toSafePoints(b?.sortOrder);

      if (sortA !== sortB) {
        return sortA - sortB;
      }

      return String(a?.name || "").localeCompare(String(b?.name || ""));
    });
}

/**
 * ✅ Calculates the total available points from active scoring factors.
 */
export function calculateAvailablePoints(scoringFactors = []) {
  return getActiveScoringFactors(scoringFactors).reduce((total, factor) => {
    return total + toSafePoints(factor?.maxPoints);
  }, 0);
}

/**
 * ✅ Validates that active scoring factors equal the required ballot total.
 *
 * Default is 100 points for the first Points / Scorecard version.
 */
export function validateScoringFactorsTotal(
  scoringFactors = [],
  maxBallotPoints = 100
) {
  const totalAvailablePoints = calculateAvailablePoints(scoringFactors);

  return {
    isValid: totalAvailablePoints === maxBallotPoints,
    totalAvailablePoints,
    pointsRemaining: maxBallotPoints - totalAvailablePoints,
    maxBallotPoints,
    message:
      totalAvailablePoints === maxBallotPoints
        ? `Scoring factors total ${maxBallotPoints} points.`
        : `Scoring factors currently total ${totalAvailablePoints} points. They must total exactly ${maxBallotPoints} points.`,
  };
}

/**
 * ✅ Validates a single factor score.
 */
export function validateFactorScore(score = {}) {
  const pointsAwarded = toSafePoints(score?.pointsAwarded);
  const maxPoints = toSafePoints(score?.maxPoints);

  if (!score?.factorId) {
    return {
      isValid: false,
      message: "Missing scoring factor ID.",
    };
  }

  if (pointsAwarded < 0) {
    return {
      isValid: false,
      message: "Points awarded cannot be less than 0.",
    };
  }

  if (pointsAwarded > maxPoints) {
    return {
      isValid: false,
      message: `${score?.factorName || "Scoring factor"} cannot exceed ${maxPoints} points.`,
    };
  }

  return {
    isValid: true,
    message: "Factor score is valid.",
  };
}

/**
 * ✅ Calculates total points for one completed points ballot.
 */
export function calculatePointsBallotTotal(scores = []) {
  return scores.reduce((total, score) => {
    return total + toSafePoints(score?.pointsAwarded);
  }, 0);
}

/**
 * ✅ Validates one complete points ballot before save.
 */
export function validatePointsBallot(
  scores = [],
  scoringFactors = [],
  maxBallotPoints = 100
) {
  const activeFactors = getActiveScoringFactors(scoringFactors);

  if (!activeFactors.length) {
    return {
      isValid: false,
      totalPoints: 0,
      message: "No scoring factors have been configured for this event.",
    };
  }

  const factorTotalValidation = validateScoringFactorsTotal(
    scoringFactors,
    maxBallotPoints
  );

  if (!factorTotalValidation.isValid) {
    return {
      isValid: false,
      totalPoints: 0,
      message: factorTotalValidation.message,
    };
  }

  for (const factor of activeFactors) {
    const matchingScore = scores.find((score) => score?.factorId === factor?.id);

    if (!matchingScore) {
      return {
        isValid: false,
        totalPoints: calculatePointsBallotTotal(scores),
        message: `Missing score for ${factor?.name}.`,
      };
    }

    const scoreValidation = validateFactorScore(matchingScore);

    if (!scoreValidation.isValid) {
      return {
        isValid: false,
        totalPoints: calculatePointsBallotTotal(scores),
        message: scoreValidation.message,
      };
    }
  }

  const totalPoints = calculatePointsBallotTotal(scores);

  if (totalPoints > maxBallotPoints) {
    return {
      isValid: false,
      totalPoints,
      message: `Total points cannot exceed ${maxBallotPoints}.`,
    };
  }

  return {
    isValid: true,
    totalPoints,
    message: "Points ballot is valid.",
  };
}

/**
 * ✅ Aggregates points votes by entry.
 */
export function aggregatePointsByEntry(votes = []) {
  const entryMap = {};

  votes.forEach((vote) => {
    if (!vote?.cid) {
      return;
    }

    if (!entryMap[vote.cid]) {
      entryMap[vote.cid] = {
        cid: vote.cid,
        voteCount: 0,
        totalPointsAwarded: 0,
        averagePoints: 0,
        votes: [],
      };
    }

    const totalPoints = toSafePoints(vote?.totalPoints);

    entryMap[vote.cid].voteCount += 1;
    entryMap[vote.cid].totalPointsAwarded += totalPoints;
    entryMap[vote.cid].votes.push(vote);
  });

  return Object.values(entryMap).map((entry) => {
    const averagePoints =
      entry.voteCount > 0
        ? entry.totalPointsAwarded / entry.voteCount
        : 0;

    return {
      ...entry,
      averagePoints,
      averagePointsDisplay: averagePoints.toFixed(2),
    };
  });
}

/**
 * ✅ Generates overall leaderboard placements for points voting.
 */
export function generatePointsOverallLeaderboard(votes = [], limit = 3) {
  return aggregatePointsByEntry(votes)
    .sort((a, b) => {
      if (b.averagePoints !== a.averagePoints) {
        return b.averagePoints - a.averagePoints;
      }

      return b.voteCount - a.voteCount;
    })
    .slice(0, limit)
    .map((entry, index) => ({
      ...entry,
      placement: index + 1,
    }));
}

/**
 * ✅ Aggregates points by scoring factor and entry.
 */
export function aggregatePointsByFactor(votes = []) {
  const factorMap = {};

  votes.forEach((vote) => {
    if (!vote?.cid || !Array.isArray(vote?.scores)) {
      return;
    }

    vote.scores.forEach((score) => {
      const factorId = score?.factorId;

      if (!factorId) {
        return;
      }

      if (!factorMap[factorId]) {
        factorMap[factorId] = {
          factorId,
          factorName: score?.factorName || "Unnamed Factor",
          maxPoints: toSafePoints(score?.maxPoints),
          entries: {},
        };
      }

      if (!factorMap[factorId].entries[vote.cid]) {
        factorMap[factorId].entries[vote.cid] = {
          cid: vote.cid,
          factorId,
          factorName: score?.factorName || "Unnamed Factor",
          maxPoints: toSafePoints(score?.maxPoints),
          voteCount: 0,
          totalPointsAwarded: 0,
          averagePoints: 0,
        };
      }

      factorMap[factorId].entries[vote.cid].voteCount += 1;
      factorMap[factorId].entries[vote.cid].totalPointsAwarded += toSafePoints(
        score?.pointsAwarded
      );
    });
  });

  return Object.values(factorMap).map((factor) => {
    const entries = Object.values(factor.entries).map((entry) => {
      const averagePoints =
        entry.voteCount > 0
          ? entry.totalPointsAwarded / entry.voteCount
          : 0;

      return {
        ...entry,
        averagePoints,
        averagePointsDisplay: averagePoints.toFixed(2),
      };
    });

    return {
      ...factor,
      entries,
    };
  });
}

/**
 * ✅ Generates Top 3 results per scoring factor.
 */
export function generatePointsFactorLeaderboards(votes = [], limit = 3) {
  return aggregatePointsByFactor(votes).map((factor) => {
    const topEntries = factor.entries
      .sort((a, b) => {
        if (b.averagePoints !== a.averagePoints) {
          return b.averagePoints - a.averagePoints;
        }

        return b.voteCount - a.voteCount;
      })
      .slice(0, limit)
      .map((entry, index) => ({
        ...entry,
        placement: index + 1,
      }));

    return {
      factorId: factor.factorId,
      factorName: factor.factorName,
      maxPoints: factor.maxPoints,
      topEntries,
    };
  });
}

/**
 * ✅ Hydrates leaderboard rows with entry/car data.
 */
export function hydratePointsLeaderboardRows(rows = [], entries = []) {
  return rows.map((row) => {
    const matchingEntry = entries.find((entry) => entry?.id === row?.cid);

    return {
      ...row,
      entry: matchingEntry || null,
      regcnt: matchingEntry?.regcnt || "",
      description: matchingEntry?.description || "",
      owner:
        matchingEntry?.owner ||
        `${matchingEntry?.ownerFirst || ""} ${matchingEntry?.ownerLast || ""}`.trim(),
    };
  });
}

/**
 * ✅ Builds the complete points results package.
 */
export function buildPointsResults({ votes = [], entries = [], limit = 3 } = {}) {
  const overallTopEntries = hydratePointsLeaderboardRows(
    generatePointsOverallLeaderboard(votes, limit),
    entries
  );

  const factorLeaderboards = generatePointsFactorLeaderboards(votes, limit).map(
    (factor) => ({
      ...factor,
      topEntries: hydratePointsLeaderboardRows(factor.topEntries, entries),
    })
  );

  return {
    overallTopEntries,
    factorLeaderboards,
  };
}